import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';
import { a as addPrevNextButtonClickHandlers } from './arrow-buttons-CGj8dylI.js';

/**
 * @param {HTMLElement} reviewRoot
 * @returns {{ viewport: HTMLElement, container: HTMLElement, slideNodes: HTMLElement[] } | undefined}
 */
function getReviewSliderElements(reviewRoot) {
  const viewport = reviewRoot.querySelector('[data-content-blocks-slider-review-viewport]');
  const container = reviewRoot.querySelector('[data-content-blocks-slider-review-container]');
  if (!viewport || !container) return undefined;

  const slideNodes = [...container.querySelectorAll('[data-content-blocks-slider-review-slide]')];
  if (slideNodes.length <= 1) return undefined;

  return { viewport, container, slideNodes };
}

/**
 * @param {HTMLElement} reviewRoot
 * @param {import('embla-carousel').EmblaCarouselType} embla
 */
function bindReviewNav(reviewRoot, embla) {
  const nav = reviewRoot.querySelector('[data-content-blocks-slider-review-nav]');
  const prevBtn = nav?.querySelector('[data-embla-button="prev"]');
  const nextBtn = nav?.querySelector('[data-embla-button="next"]');

  if (prevBtn && nextBtn) {
    addPrevNextButtonClickHandlers(embla, prevBtn, nextBtn);
  }
}

/**
 * @param {HTMLElement} reviewRoot
 * @returns {import('embla-carousel').EmblaCarouselType | undefined}
 */
function initReviewSlider(reviewRoot) {
  const elements = getReviewSliderElements(reviewRoot);
  if (!elements) return undefined;

  const { viewport, container, slideNodes } = elements;

  const embla = EmblaCarousel(viewport, {
    container,
    slides: slideNodes,
    dragFree: false,
    draggable: false,
    dragThreshold: 10,
    slidesToScroll: 1,
    align: 'start',
    containScroll: 'trim',
    direction: document.dir || 'ltr',
    active: true,
  });

  bindReviewNav(reviewRoot, embla);

  requestAnimationFrame(() => {
    embla.reInit();
  });

  return embla;
}

class ContentBlocksSlider extends HTMLElement {
  /** @type {import('embla-carousel').EmblaCarouselType | undefined} */
  #embla;

  /** @type {import('embla-carousel').EmblaCarouselType[]} */
  #reviewEmblas = [];

  connectedCallback() {
    requestAnimationFrame(() => {
      this.#initMain();
      this.#initReviews();
    });
  }

  disconnectedCallback() {
    this.#embla?.destroy();
    this.#reviewEmblas.forEach((embla) => embla.destroy());
    this.#reviewEmblas = [];
  }

  #initMain() {
    const viewport = this.querySelector('[data-content-blocks-slider-viewport]');
    if (!viewport) return;

    this.#embla = EmblaCarousel(viewport, {
      slides: '[data-content-blocks-slider-slide]',
      align: 'start',
      containScroll: 'trim',
      dragFree: true,
      direction: document.dir || 'ltr',
      breakpoints: {
        '(min-width: 1024px)': {
          active: false,
          dragFree: false,
        },
      },
    });
  }

  #initReviews() {
    this.querySelectorAll('[data-content-blocks-slider-review]').forEach((reviewRoot) => {
      const embla = initReviewSlider(reviewRoot);
      if (embla) this.#reviewEmblas.push(embla);
    });
  }
}

customElements.define('content-blocks-slider', ContentBlocksSlider);
